import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { navigateUrl } from '@app/shared/geo';
import { MONTHS, monthGrid, startOfMonth, todayInWarsaw, WEEKDAYS_SHORT, weekdayIndex } from '../events/calendar';
import { monthCells, MY_QUESTIONS_KEY, myEntriesKey, panelDays, panelShift, selectionAfterClick, type MyQuestion, type Selection } from './myCalendar';
import { MyEvents } from './MyEvents';
import { HistoryDialog, QuestionHistory } from '../requests/QuestionHistory';
import { addDays } from '../events/labels';
import { MyMonth } from './MyMonth';
import { MyDayPanel } from './MyDayPanel';
import { MyQuestions } from './MyQuestions';
import { WithdrawAsk } from './WithdrawAsk';
import { useMyMonth, useMyQuestions, useMySaves } from './useMyCalendar';
import './my-calendar.css';
import { EVENT_STATUSES, isClosed, warsawParts, type EventStatus } from '../events/labels';
import { MyTasksPage } from '../schedule/MyTasksPage';
import { ASSIGNMENT_STATUSES, CREW_KEY, hoursText, type AssignmentStatus } from './types';
import { MySettlements } from '../settlements/MySettlements';
import { MyContracts } from '../contracts/MyContracts';
import { CONTRACTS_RESOURCE } from '../contracts/types';
import { SETTLEMENTS_RESOURCE } from '../settlements/types';
import { useMe } from '../auth/useMe';
import { can } from '../auth/me';
import './crew.css';
import { DialogLayer, useEscapeLayer } from '../ui/Dialog';
import { NewPrivateTaskDialog, PrivateTaskDialog } from '../private-tasks/PrivateTaskDialog';

/** One of my assignments as `/api/me/calendar` sends it (both parts filtered by my field rules: hidden ones are absent). */
export type MyAssignment = {
  id: string;
  functionName?: string;
  status?: AssignmentStatus;
  notes?: string;
  /** Absent when the role may see neither the own hours nor the event's dates. */
  startsAt?: string;
  endsAt?: string;
  wholeEvent: boolean;
  canOpen: boolean;
  /** The days this entry falls on (as the grid counts them); absent with the dates hidden. */
  days?: string[];
  /** Spec 2026-10-22-mapy-google §5: when the crew leaves the warehouse for this term (from the schedule). */
  departureAt?: string;
  event: {
    id: string;
    name?: string;
    startsAt?: string;
    endsAt?: string;
    location?: string;
    status?: EventStatus;
    /** The place for "Nawiguj" and the notes for the crew; absent when the role may not see them. */
    locationAddress?: string;
    locationPoint?: { lat: number; lng: number } | null;
    googlePlaceId?: string;
    placeNotes?: string;
    /** Where the place of `googlePlaceId` is ("Miejsca wydarzeń"), with the point visible (review I4). */
    placePoint?: { lat: number; lng: number };
  };
};

const CLOCK = new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', hour: '2-digit', minute: '2-digit' });

/** "Nawiguj" for an entry: only with the address visible (spec 2026-10-22-mapy-google §6), to the place, no waypoints. */
export function entryNavigateUrl(a: MyAssignment): string | null {
  const e = a.event;
  if (e.locationAddress === undefined) return null;
  return navigateUrl({ point: e.locationPoint ?? null, address: e.locationAddress || e.location || '', placeId: e.googlePlaceId || null, placePoint: e.placePoint ?? null });
}
type MyCalendar = { person: { id: string; name: string } | null; assignments: MyAssignment[] };

/** First and last day of the month of `day`. */
export function monthRange(day: string): { from: string; to: string } {
  const from = startOfMonth(day);
  const [y, m] = from.split('-').map(Number) as [number, number];
  const to = new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
  return { from, to };
}

const shiftMonth = (day: string, dir: 1 | -1) => {
  const [y, m] = startOfMonth(day).split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1 + dir, 1)).toISOString().slice(0, 10);
};

type View = 'kalendarz' | 'lista' | 'do-zrobienia';
const VIEWS: Array<[View, string]> = [
  ['kalendarz', 'Kalendarz'],
  ['lista', 'Lista'],
  ['do-zrobienia', 'Do zrobienia'],
];
/** `?miesiac=2099-11` → its first day (anything else: none). */
const monthParam = (raw: string | null) => (raw && /^\d{4}-(0[1-9]|1[0-2])$/.test(raw) ? `${raw}-01` : null);

/**
 * "Mój kalendarz" (F19; owner 2026-10-28 21:32 / 21:37): „Kalendarz” — my month like the main calendar, with my
 * availability, my events and the questions about my days, and a day's panel to change them; „Lista” (`?widok=lista`) —
 * every question I received with my answers, then my assignments day by day; „Do zrobienia” (`?widok=do-zrobienia`,
 * where the task reminders link to). A tab here rather than a new menu entry: the menu and the phone's bottom bar stay
 * as they are (owner's wish).
 */
export function MyCalendarPage() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('widok');
  const view: View = raw === 'do-zrobienia' || raw === 'lista' ? raw : 'kalendarz';
  const { me } = useMe();
  const show = (next: View) =>
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        if (next === 'kalendarz') out.delete('widok');
        else out.set('widok', next);
        return out;
      },
      { replace: true },
    );
  return (
    <section className="page" data-feature-id="crew.my_calendar">
      <div className="page-head">
        <h1>Mój kalendarz</h1>
      </div>
      <div role="tablist" aria-label="Widok" className="segment my-cal-tabs">
        {VIEWS.map(([v, label]) => (
          <button key={v} role="tab" type="button" aria-selected={view === v} onClick={() => show(v)}>
            {label}
          </button>
        ))}
      </div>
      {view === 'do-zrobienia' ? <MyTasksPage /> : view === 'lista' ? <MyListView /> : <MyCalendarView />}
      {view !== 'do-zrobienia' && me && can(me, SETTLEMENTS_RESOURCE, 'view') && <MySettlements />}
      {view !== 'do-zrobienia' && me && can(me, CONTRACTS_RESOURCE, 'view') && <MyContracts />}
    </section>
  );
}

/**
 * The parts of the page a click may hit without closing the day's panel or clearing the selection (owner 2026-10-29
 * 00:21: „kliknięcie poza okienkiem wyłącza je”): the month (another day switches the panel), the panel, the bar of
 * the selected days, the dialogs and the notices — as the grid's `KEEPS_SELECTION`.
 */
const KEEPS_PANEL = '.mc-month, .mc-panel, .mc-selbar, .mc-done, .notice-area, .dialog-backdrop, [role="dialog"], [role="alertdialog"]';

const NO_PERSON = 'Twoje konto nie jest jeszcze połączone z osobą z listy „Osoby”, więc nie masz tu kalendarza dostępności ani zapytań. Poproś właściciela o połączenie (Osoby → Konto w aplikacji).';

/** The month: my days, the day's panel, several days at once (Cmd / Alt), the withdrawal question. */
function MyCalendarView() {
  const today = todayInWarsaw();
  const [params, setParams] = useSearchParams();
  const anchor = monthParam(params.get('miesiac')) ?? startOfMonth(today);
  const setAnchor = (day: string) =>
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        out.set('miesiac', day.slice(0, 7));
        return out;
      },
      { replace: true },
    );
  const grid = monthGrid(anchor);
  const range = { from: grid[0]![0]!, to: grid.at(-1)!.at(-1)! };
  const { marks, calendar, questions, tasks } = useMyMonth(range);
  // owner 2026-11-03 23:45: „+ Zadanie prywatne” of a day / a click on one of my tasks — the dialogs of „Moje zadania”
  const [taskDialog, setTaskDialog] = useState<{ newOn: string } | { id: string; day: string | null } | null>(null);
  // review M1: a task ticked off or deleted from its chip leaves the month with the focus on it — the focus goes to its
  // day once the month has my tasks again (only while nothing else took the focus meanwhile)
  const refocusDay = useRef<string | null>(null);
  const openTask = (id: string) => setTaskDialog({ id, day: tasks.data?.tasks.find((t) => t.id === id)?.dueDay ?? null });
  const closeTask = () => {
    if (taskDialog && 'id' in taskDialog && taskDialog.day) {
      refocusDay.current = taskDialog.day;
      const forget = () => (refocusDay.current = null);
      document.addEventListener('pointerdown', forget, { once: true, capture: true });
      document.addEventListener('keydown', forget, { once: true, capture: true });
    }
    setTaskDialog(null);
  };
  useEffect(() => {
    const d = refocusDay.current;
    if (!d) return;
    refocusDay.current = null;
    const now = document.activeElement;
    if (!now || now === document.body || !now.isConnected || now.tagName === 'MAIN') document.querySelector<HTMLElement>(`.mc-month [data-day="${d}"]`)?.focus();
  }, [tasks.dataUpdatedAt]);
  const [sel, setSel] = useState<Selection>({ selected: [], open: null });
  // a save from the bar clears the selection; one from the panel of several days keeps it (the panel stays on them)
  const saves = useMySaves(() => setSel((s) => (panelDays(s).length > 1 ? s : { ...s, selected: [] })));
  const selecting = sel.selected.length > 0;
  const open = sel.open !== null;
  // a click outside the month and the bar clears the selection, as in the grid; Esc too
  useEscapeLayer(() => setSel((s) => ({ ...s, selected: [] })), selecting);
  // owner 2026-10-29 00:21: a click outside the panel (and the month) closes the panel and clears the selection
  useEffect(() => {
    if (!selecting && !open) return;
    const away = (e: PointerEvent) => {
      if (e.target instanceof Element && e.target.closest(KEEPS_PANEL)) return;
      setSel({ selected: [], open: null });
    };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [selecting, open]);
  // owner 00:53: the panel is docked to the right edge and the month keeps its width — when its right edge would be
  // under the panel, the page scrolls sideways just that far while the panel is open (and back when it closes)
  useLayoutEffect(() => {
    if (!open || !window.matchMedia?.('(min-width: 900px)').matches) return;
    const month = document.querySelector('.mc-month');
    const shift = month ? panelShift(month.getBoundingClientRect(), document.documentElement.clientWidth) : 0;
    if (shift === 0) return;
    window.scrollBy({ left: shift });
    return () => window.scrollBy({ left: -shift });
  }, [open]);
  // review M9: the focus goes back to the day when its panel closes
  const focusDay = (day: string) => setTimeout(() => document.querySelector<HTMLElement>(`.mc-month [data-day="${day}"]`)?.focus(), 0);
  const onDay = (day: string, add: boolean) => {
    // owner 00:21: a second plain click on the day of the open panel closes it
    if (!add && sel.open === day) focusDay(day);
    setSel((s) => selectionAfterClick(s, day, add, today));
  };
  const [y, m] = anchor.split('-').map(Number) as [number, number];
  const error = marks.error ?? calendar.error ?? tasks.error;
  if (marks.data && marks.data.person === null) return <p className="notice">{NO_PERSON}</p>;
  const weeks = monthCells(anchor, today, marks.data?.days ?? [], calendar.data?.assignments ?? [], questions.data?.questions ?? [], questions.data?.awaiting ?? [], tasks.data?.tasks ?? []);
  const cells = weeks.flat();
  const openCell = sel.open ? cells.find((c) => c.day === sel.open) : undefined;
  const openDays = panelDays(sel);
  const markSelected = (state: 'available' | 'unavailable' | null) => saves.mark({ days: sel.selected, state });
  return (
    <div className="mc">
      {error && <p className="alert" role="alert">{error instanceof ApiError ? error.message : 'Nie udało się wczytać kalendarza'}</p>}
      <div className="cal-nav mc-nav">
        <button className="btn icon" type="button" aria-label="Poprzedni miesiąc" onClick={() => setAnchor(shiftMonth(anchor, -1))}>
          ‹
        </button>
        <button className="btn icon" type="button" aria-label="Następny miesiąc" onClick={() => setAnchor(shiftMonth(anchor, 1))}>
          ›
        </button>
        <h2 aria-live="polite">
          {MONTHS[m - 1]} {y}
        </h2>
        <button className="btn small" type="button" onClick={() => setAnchor(startOfMonth(today))}>
          Dziś
        </button>
      </div>
      {selecting && (
        <div className="mc-selbar" role="toolbar" aria-label="Zaznaczone dni">
          <span>
            Zaznaczone: <strong>{sel.selected.length === 1 ? '1 dzień' : `${sel.selected.length} dni`}</strong>
          </span>
          <button type="button" className="btn small" disabled={saves.marking} onClick={() => markSelected('available')}>
            Dostępny
          </button>
          <button type="button" className="btn small" disabled={saves.marking} onClick={() => markSelected('unavailable')}>
            Niedostępny
          </button>
          <button type="button" className="btn small ghost" disabled={saves.marking} onClick={() => markSelected(null)}>
            Wyczyść
          </button>
          <button type="button" className="btn small ghost" onClick={() => setSel((s) => ({ ...s, selected: [] }))}>
            Odznacz
          </button>
        </div>
      )}
      {selecting && !openCell && saves.markError && <p className="alert" role="alert">{saves.markError}</p>}
      {saves.done && (
        <p className="notice mc-done" role="status">
          {saves.done}{' '}
          <button type="button" className="btn small ghost" onClick={saves.clearDone}>
            OK
          </button>
        </p>
      )}
      <div className={`mc-layout${openCell ? ' with-panel' : ''}`} aria-busy={marks.isFetching || calendar.isFetching || tasks.isFetching}>
        <MyMonth weeks={weeks} selected={sel.selected} open={sel.open} onDay={onDay} onTask={openTask} />
        {openCell && (
          <MyDayPanel
            key={openDays.join(',')}
            cell={openCell}
            days={openDays}
            cells={cells}
            today={today}
            questions={questions.data?.questions ?? []}
            marking={saves.marking}
            markError={saves.markError}
            onMark={saves.mark}
            answering={saves.answering}
            answerError={saves.answerError}
            onAnswer={saves.answer}
            onNewTask={(d) => setTaskDialog({ newOn: d })}
            onTask={openTask}
            onClose={() => {
              setSel((s) => ({ ...s, open: null }));
              focusDay(openCell.day);
            }}
          />
        )}
      </div>
      <ul className="pg-legend mc-legend" aria-label="Legenda">
        <li><i className="mc-swatch available">D</i> dostępny</li>
        <li><i className="mc-swatch unavailable">N</i> niedostępny (godziny — tylko część dnia)</li>
        <li><i className="mc-swatch asked">?</i> zapytanie czeka na odpowiedź</li>
        <li><i className="ep-dot" data-tone="accent" /> proponowany</li>
        <li><i className="ep-dot" data-tone="success" /> potwierdzony</li>
        <li><i className="ep-dot" data-tone="danger" /> odmowa</li>
        <li><i className="mc-swatch task" /> zadanie prywatne</li>
      </ul>
      <p className="hint">Kliknij dzień, żeby oznaczyć dostępność, odpowiedzieć na zapytanie albo dodać zadanie prywatne. Kilka dni naraz: zaznacz je z ⌘ / Alt, potem kliknij jeden z nich. Ponowne kliknięcie dnia albo kliknięcie obok zamyka panel.</p>
      {taskDialog && 'newOn' in taskDialog && (
        <NewPrivateTaskDialog start={{ title: '', dueDay: taskDialog.newOn, dueTime: null }} onClose={() => setTaskDialog(null)} onCreated={() => setTaskDialog(null)} />
      )}
      {taskDialog && 'id' in taskDialog && <PrivateTaskDialog taskId={taskDialog.id} onClose={closeTask} />}
      {saves.ask && <WithdrawAsk message={saves.ask.message} busy={saves.marking} onAnswer={saves.answerAsk} onClose={saves.closeAsk} />}
      {marks.data?.person && <PhoneCalendar />}
    </div>
  );
}

/**
 * „Lista” (owner 2026-10-30 21:38 „najpierw są wszystkie wydarzenia, w jakich osoba bierze udział, a później aktualna
 * lista zapytań”; 21:49 „pełna historia … w historii (jakiś guziczek)”): „Historia” on top, „Moje wydarzenia” from today
 * on, then my active questions with their newest point; the phone's calendar below.
 */
function MyListView() {
  const today = todayInWarsaw();
  const questions = useMyQuestions();
  const saves = useMySaves();
  const [history, setHistory] = useState(false);
  // a year ahead: every event I take part in from today on
  const range = { from: today, to: addDays(today, 365) };
  const calendar = useQuery({ queryKey: myEntriesKey(range), queryFn: () => api<MyCalendar>(`/api/me/calendar?from=${range.from}&to=${range.to}`) });
  const person = calendar.data?.person ?? questions.data?.person;
  return (
    <div className="mc-lista">
      {person && (
        <div className="mc-lista-head">
          <button type="button" className="btn small" onClick={() => setHistory(true)} data-feature-id="crew.my_calendar">
            Historia
          </button>
        </div>
      )}
      {calendar.error && <p className="alert" role="alert">{calendar.error instanceof ApiError ? calendar.error.message : 'Nie udało się wczytać Twoich wydarzeń'}</p>}
      {calendar.data && calendar.data.person === null && (
        <p className="notice">Twoje konto nie jest jeszcze połączone z osobą z listy „Osoby”, więc nie masz wydarzeń ani zapytań. Poproś właściciela o połączenie (Osoby → Konto w aplikacji).</p>
      )}
      {calendar.data?.person && <MyEvents assignments={calendar.data.assignments} now={new Date()} />}
      {questions.data?.person && (
        <section aria-label="Zapytania">
          <MyQuestions questions={questions.data.questions} today={today} answering={saves.answering} answerError={saves.answerError} onAnswer={saves.answer} />
        </section>
      )}
      {calendar.data?.person && <PhoneCalendar />}
      {history && <MyHistory onClose={() => setHistory(false)} />}
    </div>
  );
}

/** „Historia” of „Lista”: all my questions — inactive ones and those of deleted events too — read-only. */
function MyHistory({ onClose }: { onClose: () => void }) {
  const all = useQuery({ queryKey: [...MY_QUESTIONS_KEY, 'history'], queryFn: () => api<{ questions: MyQuestion[] }>('/api/me/questions?history=1') });
  return (
    <HistoryDialog onClose={onClose} loading={all.isLoading} error={all.error ? (all.error instanceof ApiError ? all.error.message : 'Nie udało się wczytać historii') : null}>
      {all.data && <QuestionHistory items={all.data.questions} mine />}
    </HistoryDialog>
  );
}

export function MyItem({ a }: { a: MyAssignment }) {
  const name = a.event.name || 'Wydarzenie';
  const cancelled = isClosed(a.event.status);
  const go = cancelled ? null : entryNavigateUrl(a);
  return (
    <div className={`my-item${cancelled ? ' cancelled' : ''}`}>
      <span className="my-item-name">{a.canOpen ? <Link to={`/wydarzenia?wydarzenie=${a.event.id}`}>{name}</Link> : name}</span>
      <span className="my-item-meta">
        {a.functionName && <span>{a.functionName}</span>}
        {a.startsAt && <span>{a.wholeEvent ? hoursText(a.startsAt, a.endsAt) : `${hoursText(a.startsAt, a.endsAt)} (część wydarzenia)`}</span>}
        {a.event.location && <span>{a.event.location}</span>}
        {a.status && <span>{ASSIGNMENT_STATUSES[a.status]}</span>}
        {cancelled && a.event.status && <span>{a.event.status === 'anulowane' ? `Wydarzenie ${EVENT_STATUSES.anulowane.toLowerCase()}` : EVENT_STATUSES[a.event.status]}</span>}
      </span>
      {a.notes && <span className="my-item-meta">{a.notes}</span>}
      {!cancelled && (a.departureAt || go) && (
        <span className="my-item-meta my-item-travel" data-feature-id="schedule.departure">
          {a.departureAt && <strong>Wyjazd {CLOCK.format(new Date(a.departureAt))}</strong>}
          {go && (
            <a className="btn small" href={go} target="_blank" rel="noreferrer" data-feature-id="events.route">
              Nawiguj
            </a>
          )}
        </span>
      )}
      {!cancelled && a.event.placeNotes && <span className="my-item-meta">Miejsce: {a.event.placeNotes}</span>}
    </div>
  );
}

/** "Dodaj do kalendarza w telefonie": a private subscription address (webcal://… opens the phone's calendar app). */
function PhoneCalendar() {
  const queryClient = useQueryClient();
  const key = [...CREW_KEY, 'token'];
  const token = useQuery({ queryKey: key, queryFn: () => api<{ path: string | null }>('/api/me/calendar-token') });
  const [confirmNew, setConfirmNew] = useState(false);
  const [copied, setCopied] = useState(false);
  const create = useMutation({
    mutationFn: () => api<{ path: string }>('/api/me/calendar-token', { method: 'POST' }),
    onSuccess: (data) => {
      queryClient.setQueryData(key, data);
      setConfirmNew(false);
      setCopied(false);
    },
  });
  const path = token.data?.path ?? null;
  const https = path ? `${window.location.origin}${path}` : '';
  const webcal = path ? `webcal://${window.location.host}${path}` : '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(https);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <section className="my-phone" aria-labelledby="my-phone-title" data-feature-id="crew.ics">
      <h2 id="my-phone-title">Kalendarz w telefonie</h2>
      <p className="hint">Twoje przydziały pojawią się w kalendarzu telefonu i będą się same aktualizować. Adres jest prywatny — nie przekazuj go nikomu.</p>
      {token.data && !path && (
        <div className="actions">
          <button
            className="btn primary"
            type="button"
            disabled={create.isPending}
            onClick={() =>
              create.mutate(undefined, {
                onSuccess: (data) => {
                  window.location.href = `webcal://${window.location.host}${data.path}`;
                },
              })
            }
          >
            Dodaj do kalendarza w telefonie
          </button>
        </div>
      )}
      {path && (
        <>
          <div className="actions">
            <a className="btn primary" href={webcal}>
              Dodaj do kalendarza w telefonie
            </a>
            <button className="btn" type="button" onClick={() => void copy()}>
              {copied ? 'Skopiowano' : 'Kopiuj adres'}
            </button>
            <button className="btn ghost" type="button" onClick={() => setConfirmNew(true)}>
              Nowy adres (wyłącz stary)
            </button>
          </div>
          <input className="my-phone-url" type="text" readOnly value={https} aria-label="Adres kalendarza" onFocus={(e) => e.currentTarget.select()} />
          <p className="hint">Android (Kalendarz Google): skopiuj adres i dodaj go na calendar.google.com → „Inne kalendarze” → „Z adresu URL”.</p>
        </>
      )}
      {create.error && <p className="alert" role="alert">{create.error instanceof ApiError ? create.error.message : 'Nie udało się utworzyć adresu'}</p>}
      {confirmNew && (
        <div className="dialog-backdrop">
          <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby="my-phone-new-title">
            <DialogLayer onClose={() => setConfirmNew(false)} busy={create.isPending} />
            <h2 id="my-phone-new-title">Utworzyć nowy adres?</h2>
            <p>Stary adres przestanie działać od razu. W telefonie trzeba będzie dodać kalendarz od nowa.</p>
            <div className="actions">
              <button className="btn primary" type="button" disabled={create.isPending} onClick={() => create.mutate()}>
                Utwórz nowy adres
              </button>
              <button className="btn" type="button" onClick={() => setConfirmNew(false)}>
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
